import { CollapsibleFilters } from '../../../components/common/CollapsibleFilters';
import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Select,
  Option,
  FormControl,
  FormLabel,
  Input,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from '@mui/joy'
import {
  TrendUp as TrendingUp,
  Ranking as Leaderboard,
  ChartLine as Timeline,
  Code as Source,
  ChartBar as Assessment,
  PersonSimpleWalk as DirectionsWalk,
  DownloadSimple as Download,
  ArrowLeft,
  Trophy as EmojiEvents,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { getDateRangePresets, useExportSalesReport } from '../../../hooks/useSalesReports'
import type { DateRangePresetKey } from '../../../hooks/useSalesReports'
import SalesOverview from './SalesOverview'
import PipelineAnalytics from './PipelineAnalytics'
import RepPerformance from './RepPerformance'
import LeadSourceROI from './LeadSourceROI'
import SalesTrends from './SalesTrends'
import CanvasingMetrics from './CanvasingMetrics'
import SalesLeaderboard from './SalesLeaderboard'

export default function SalesReportsPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')
  const presets = getDateRangePresets()
  const [dateRange, setDateRange] = useState({
    from: presets.thisMonth.from,
    to: presets.thisMonth.to,
  })
  const [selectedPreset, setSelectedPreset] = useState<DateRangePresetKey | 'custom'>('thisMonth')

  const exportMutation = useExportSalesReport()

  const handlePresetChange = (preset: DateRangePresetKey | 'custom') => {
    setSelectedPreset(preset)
    if (preset !== 'custom' && presets[preset]) {
      setDateRange({
        from: presets[preset].from,
        to: presets[preset].to,
      })
    }
  }

  const handleExport = () => {
    const reportType = activeTab === 'canvasing' ? 'canvasing' : activeTab as any
    exportMutation.mutate({
      report_type: reportType,
      date_from: dateRange.from,
      date_to: dateRange.to,
    })
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Back Button */}
      <Button
        variant="plain"
        color="neutral"
        startDecorator={<ArrowLeft />}
        onClick={() => navigate('/reports')}
        sx={{ mb: 3 }}
      >
        Back to Reports
      </Button>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography level="h1" sx={{ fontSize: { xs: '1.5rem', md: '2.25rem' } }}>
          Sales Reports
        </Typography>
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          Analyze sales performance, pipeline health, and team productivity
        </Typography>
      </Box>

      {/* Date Range Filter */}
      <CollapsibleFilters><Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'flex-end' }}
          >
            <FormControl sx={{ minWidth: { md: 150 } }}>
              <FormLabel>Period</FormLabel>
              <Select
                value={selectedPreset}
                onChange={(_, value) => handlePresetChange(value as DateRangePresetKey | 'custom')}
              >
                <Option value="thisMonth">This Month</Option>
                <Option value="lastMonth">Last Month</Option>
                <Option value="thisQuarter">This Quarter</Option>
                <Option value="lastQuarter">Last Quarter</Option>
                <Option value="thisYear">This Year</Option>
                <Option value="lastYear">Last Year</Option>
                <Option value="last30Days">Last 30 Days</Option>
                <Option value="last90Days">Last 90 Days</Option>
                <Option value="custom">Custom</Option>
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1.5} sx={{ flex: { md: 'none' } }}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>From Date</FormLabel>
                <Input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => {
                    setDateRange({ ...dateRange, from: e.target.value })
                    setSelectedPreset('custom')
                  }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>To Date</FormLabel>
                <Input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => {
                    setDateRange({ ...dateRange, to: e.target.value })
                    setSelectedPreset('custom')
                  }}
                />
              </FormControl>
            </Stack>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<Download />}
              onClick={handleExport}
              loading={exportMutation.isPending}
              disabled={exportMutation.isPending}
            >
              Export CSV
            </Button>
          </Stack>
        </CardContent>
      </Card></CollapsibleFilters>

      {/* Report Tabs */}
      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as string)}>
        <TabList
          sx={{
            flexWrap: 'wrap',
            '& > *': { flex: { xs: '1 1 45%', sm: '0 0 auto' } },
          }}
        >
          <Tab value="overview">
            <Assessment style={{ marginRight: 8 }} />
            Overview
          </Tab>
          <Tab value="pipeline">
            <TrendingUp style={{ marginRight: 8 }} />
            Pipeline
          </Tab>
          <Tab value="reps">
            <Leaderboard style={{ marginRight: 8 }} />
            Rep Performance
          </Tab>
          <Tab value="sources">
            <Source style={{ marginRight: 8 }} />
            Lead Sources
          </Tab>
          <Tab value="trends">
            <Timeline style={{ marginRight: 8 }} />
            Trends
          </Tab>
          <Tab value="canvasing">
            <DirectionsWalk style={{ marginRight: 8 }} />
            Canvasing
          </Tab>
          <Tab value="leaderboard">
            <EmojiEvents style={{ marginRight: 8 }} />
            Leaderboard
          </Tab>
        </TabList>

        <TabPanel value="overview">
          <SalesOverview dateRange={dateRange} />
        </TabPanel>

        <TabPanel value="pipeline">
          <PipelineAnalytics dateRange={dateRange} />
        </TabPanel>

        <TabPanel value="reps">
          <RepPerformance dateRange={dateRange} />
        </TabPanel>

        <TabPanel value="sources">
          <LeadSourceROI dateRange={dateRange} />
        </TabPanel>

        <TabPanel value="trends">
          <SalesTrends />
        </TabPanel>

        <TabPanel value="canvasing">
          <CanvasingMetrics dateRange={dateRange} />
        </TabPanel>

        <TabPanel value="leaderboard">
          <SalesLeaderboard />
        </TabPanel>
      </Tabs>
    </Box>
  )
}
